import React, { useState, useEffect } from 'react';

const DonatePage = () => {
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [donations, setDonations] = useState([]);
  const [isFetchingLeaderboard, setIsFetchingLeaderboard] = useState(true);

  const predefinedAmounts = [10, 20, 50, 100];

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/donations/public`);
      if (response.ok) {
        const data = await response.json();
        setDonations(data || []);
      }
    } catch (err) {
      console.error('Failed to load leaderboard', err);
    } finally {
      setIsFetchingLeaderboard(false);
    }
  };

  const handleCheckout = async (selectedAmount) => {
    const amountToUse = selectedAmount || amount;
    if (!amountToUse || isNaN(amountToUse) || Number(amountToUse) < 1) {
      setError('Molimo unesite ispravan iznos (minimum 1 EUR)');
      return;
    }
    if (!name || name.trim() === '') {
      setError('Molimo unesite vaše ime.');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          amount: String(amountToUse),
          name: name,
          isAnonymous: isAnonymous
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to initialize checkout');
      }

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('Could not retrieve secure checkout link.');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Nešto je pošlo po zlu. Pokušajte ponovo.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="min-h-screen pt-32 pb-20 bg-midnight text-warm-white relative overflow-hidden">
      {/* Background Graphic */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="absolute left-0 right-0 top-0 -z-10 m-auto h-[310px] w-[310px] rounded-full bg-electric-yellow opacity-20 blur-[100px]"></div>
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
        
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-clash font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-electric-yellow to-white mb-6">
            Podrži Unframed Talks
          </h1>
          <p className="text-xl text-soft-gray max-w-2xl mx-auto">
            Vaša donacija nam pomaže da snimamo bolje epizode, dovodimo sjajne goste i održavamo produkciju na najvišem nivou. Hvala na povjerenju!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          
          {/* Left Column: Donation Form */}
          <div className="bg-charcoal/80 backdrop-blur-md rounded-3xl p-8 border border-slate-dark/50 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-electric-yellow/10 rounded-full blur-3xl"></div>
            
            <h2 className="text-2xl font-bold text-warm-white mb-6 font-clash">Pomozi nam da rastemo</h2>
            
            {error && (
              <div className="mb-6 bg-red-500/10 border border-red-500/50 text-red-500 px-4 py-3 rounded-xl text-sm font-medium">
                {error}
              </div>
            )}

            <div className="space-y-6">
              {/* Name Input */}
              <div className="relative">
                <label className="block text-electric-yellow font-medium mb-3">
                  Tvoje Ime
                </label>
                <input
                  type="text"
                  placeholder="Unesite vaše ime"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-midnight border border-slate-dark/50 rounded-xl py-3 px-4 text-warm-white placeholder-slate-dark focus:outline-none focus:border-electric-yellow focus:ring-1 focus:ring-electric-yellow transition-all mb-3"
                />
                
                <label className="flex items-center space-x-3 cursor-pointer group w-fit mt-3">
                  <div className="relative flex items-center justify-center">
                    <input 
                      type="checkbox" 
                      className="sr-only"
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                    />
                    <div className={`w-5 h-5 rounded-md border transition-colors flex items-center justify-center ${isAnonymous ? 'bg-electric-yellow border-electric-yellow' : 'bg-midnight border-slate-dark group-hover:border-electric-yellow/50'}`}>
                      {isAnonymous && (
                        <svg className="w-3.5 h-3.5 text-midnight" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-soft-gray group-hover:text-warm-white transition-colors text-sm">Želim da moja donacija bude javno anonimna</span>
                </label>
              </div>

              {/* Predefined Amounts */}
              <div>
                <label className="block text-electric-yellow font-medium mb-3">
                  Izaberi iznos (EUR)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {predefinedAmounts.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => handleCheckout(preset)}
                      disabled={isLoading}
                      className="py-3 px-4 rounded-xl border border-slate-dark/50 hover:border-electric-yellow hover:text-electric-yellow text-warm-white bg-midnight/50 transition-all font-medium whitespace-nowrap disabled:opacity-50"
                    >
                      €{preset}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div>
                <label className="block text-electric-yellow font-medium mb-3">
                  Ili unesite drugi iznos
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-soft-gray font-medium">€</span>
                  <input
                    type="number"
                    min="1"
                    placeholder="25"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full bg-midnight border border-slate-dark/50 rounded-xl py-3 pl-10 pr-4 text-warm-white placeholder-slate-dark focus:outline-none focus:border-electric-yellow focus:ring-1 focus:ring-electric-yellow transition-all"
                  />
                </div>
              </div>


              <button
                onClick={() => handleCheckout()}
                disabled={isLoading || !amount || !name.trim()}
                className="w-full bg-electric-yellow text-midnight font-bold py-4 rounded-xl hover:bg-electric-yellow/90 transition-all disabled:opacity-50 mt-6 text-lg"
              >
                {isLoading ? 'Učitavanje...' : 'Nastavi na plaćanje'}
              </button>
              
              <p className="text-center text-soft-gray text-xs mt-4">
                Plaćanje je sigurno i obrađuje se preko Whop platforme.
              </p>
            </div>
          </div>

          {/* Right Column: Leaderboard */}
          <div className="bg-charcoal/40 backdrop-blur-md rounded-3xl p-8 border border-slate-dark/30">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-electric-yellow mb-3 font-clash">
                Naši Podržavaoci
              </h2>
              <p className="text-soft-gray">
                Veliko hvala svima koji su do sada doprinijeli razvoju podcasta!
              </p>
            </div>

            <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
              {isFetchingLeaderboard ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-electric-yellow"></div>
                </div>
              ) : donations.length === 0 ? (
                <div className="text-center py-12 text-slate-dark font-medium border-2 border-dashed border-slate-dark/50 rounded-2xl">
                  Još nema donacija. Budite prvi!
                </div>
              ) : (
                donations.map((donation, index) => (
                  <div 
                    key={donation.id} 
                    className="flex justify-between items-center p-4 rounded-2xl bg-midnight/50 border border-slate-dark/30 hover:border-electric-yellow/30 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold font-clash ${
                        index === 0 ? 'bg-yellow-400 text-yellow-900 shadow-[0_0_15px_rgba(250,204,21,0.3)]' :
                        index === 1 ? 'bg-gray-300 text-gray-800' :
                        index === 2 ? 'bg-amber-600 text-amber-900' :
                        'bg-slate-dark text-soft-gray'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-warm-white">
                          {donation.name}
                        </div>
                        <div className="text-xs text-soft-gray mt-0.5">
                          {formatDate(donation.createdAt)}
                        </div>
                      </div>
                    </div>
                    <div className="font-clash font-bold text-electric-yellow text-lg">
                      €{Number(donation.amount).toFixed(2)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonatePage;
